import type { ChangeEvent } from 'react';
import { useRef, useCallback, useState, useEffect } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Loader } from '@mantine/core';
import { IconMailOpened, IconCheck } from '@tabler/icons-react';

import { EMAIL_OTP_LENGTH } from '~/domains/auth/consts';
import { Button } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import dt from '~/testing';

import useStyles from './styles';

function formatCountdown(seconds: number): string {
  if (seconds <= 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

export type EmailVerificationStatus = 'idle' | 'loading' | 'success' | 'error';

interface EmailVerificationFormProps {
  email: string;
  code: string;
  onCodeChange: (code: string) => void;
  onResendCode?: () => void;
  onStartOver?: () => void;
  onContactSupport?: () => void;
  verificationStatus?: EmailVerificationStatus;
  resendCooldownSeconds?: number;
  errorMessage?: string;
}

export function EmailVerificationForm({
  email,
  code,
  onCodeChange,
  onResendCode,
  onStartOver,
  onContactSupport,
  verificationStatus = 'idle',
  resendCooldownSeconds,
  errorMessage,
}: EmailVerificationFormProps) {
  const { classes, cx, theme } = useStyles();
  const { t } = useTranslation('auth');
  const inputRef = useRef<HTMLInputElement>(null);
  const cooldown = resendCooldownSeconds ?? 0;
  const [resendCountdown, setResendCountdown] = useState(cooldown);
  const [hasResent, setHasResent] = useState(false);
  const isFinalResend = hasResent && resendCooldownSeconds == null;

  const isSuccess = verificationStatus === 'success';
  const isError = verificationStatus === 'error';
  const isLoading = verificationStatus === 'loading';
  const isResendDisabled = resendCountdown > 0 || isLoading;

  useEffect(() => {
    if (resendCooldownSeconds !== undefined) {
      setResendCountdown(resendCooldownSeconds);
    }
  }, [resendCooldownSeconds]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => setResendCountdown((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/\D/g, '').slice(0, EMAIL_OTP_LENGTH);
      onCodeChange(digits);
    },
    [onCodeChange]
  );

  // Lock cursor to end of typed digits so insertion mid-value is prevented
  const lockCursor = useCallback(() => {
    const input = inputRef.current;
    if (input) {
      input.setSelectionRange(code.length, code.length);
    }
  }, [code.length]);

  const handleResendCode = () => {
    if (isResendDisabled) return;
    onResendCode?.();
    setHasResent(true);
  };

  const testIds = dt.components.shared.auth.signUpVersionTwo.emailVerificationForm;

  const renderFeedbackMessage = () => {
    if (isError) {
      const displayMessage = errorMessage || t('signUp.emailVerification.errorTitle');
      return (
        <Text
          variant="body-large"
          c="red.4"
          className={classes.countdownText}
          data-test-id={testIds.errorMessage}
          style={{ whiteSpace: 'pre-line' }}
        >
          {displayMessage}
        </Text>
      );
    }

    if (isFinalResend) {
      return (
        <Text variant="body-large" c="gray.7" className={classes.countdownText}>
          {t('signUp.emailVerification.finalResend')}{' '}
          <Button
            mode="inlineLink"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onContactSupport?.();
            }}
            style={{
              fontWeight: 400,
              textDecoration: 'underline',
            }}
          >
            {t('signUp.emailVerification.contactSupport')}
          </Button>
        </Text>
      );
    }

    if (resendCountdown > 0) {
      const countdownKey = hasResent
        ? 'signUp.emailVerification.codeResent'
        : 'signUp.emailVerification.resendIn';
      return (
        <Text variant="body-large" c="gray.7" className={classes.countdownText}>
          {t(countdownKey, {
            time: formatCountdown(resendCountdown),
          })}
        </Text>
      );
    }

    return null;
  };

  return (
    <div className={classes.emailVerificationContainer}>
      {onStartOver && (
        <Title
          variant="subhead-medium"
          c="gray2.6"
          component="button"
          type="button"
          onClick={onStartOver}
          className={classes.startOverButton}
          data-test-id={testIds.startOverButton}
        >
          {t('signUp.emailVerification.startOver')}
        </Title>
      )}
      <div className={classes.iconContainer}>
        <IconMailOpened size={28} color={theme.colors.dark[9]} />
      </div>
      <div className={classes.headerContainer}>
        <Title variant="subhead-x-large" weight={700} c="gray2.6" align="center">
          {t('signUp.headers.emailVerification.title')}
        </Title>
        <div style={{ maxWidth: 420 }}>
          <Text variant="body-large" c="gray2.4" align="center">
            {t('signUp.headers.emailVerification.copy')}{' '}
            <Text variant="body-large" c="gray2.6" fw={500} component="span">
              {email}
            </Text>
          </Text>
        </div>
      </div>
      <div className={classes.pinInputSection}>
        <div
          className={cx(classes.pinInputWrapper, isError && classes.pinInputWrapperError)}
          data-test-id={testIds.pinInput}
        >
          <input
            ref={inputRef}
            className={classes.codeInput}
            type="text"
            inputMode="numeric"
            maxLength={EMAIL_OTP_LENGTH}
            value={code}
            onChange={handleChange}
            onSelect={lockCursor}
            readOnly={isLoading || isSuccess}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            aria-label={t('signUp.headers.emailVerification.digitLabel')}
            autoComplete="one-time-code"
          />
          <div className={classes.codeDisplay} aria-hidden="true">
            {Array.from({ length: EMAIL_OTP_LENGTH }).map((_, index) => (
              <span
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                className={cx(
                  classes.codeChar,
                  code[index] ? classes.codeCharFilled : classes.codeCharEmpty,
                  isSuccess && code[index] && classes.codeCharSuccess
                )}
              >
                {code[index] || '-'}
              </span>
            ))}
          </div>
          {isLoading && (
            <div className={classes.pinInputStatus}>
              <Loader size="sm" color={theme.colors.blue[3]} />
            </div>
          )}
          {isSuccess && (
            <div className={classes.pinInputStatus}>
              <IconCheck
                size={24}
                color={theme.colors.blue[3]}
                strokeWidth={3}
                data-test-id={testIds.successIcon}
              />
            </div>
          )}
        </div>
        {renderFeedbackMessage()}
      </div>
      <div className={classes.buttonsSection}>
        {!isFinalResend && (
          <Button
            variant="text"
            onClick={handleResendCode}
            disabled={isResendDisabled}
            className={classes.resendButton}
            data-test-id={testIds.resendButton}
          >
            {t('signUp.emailVerification.resendCode')}
          </Button>
        )}
      </div>
    </div>
  );
}
